import { useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ChatPanel } from "./chat";
import { ConfirmModal } from "./ConfirmModal";
import { useCelebration } from "../hooks/useCelebration";
import { AnswerSelectionSection } from "./gameBoard/AnswerSelectionSection";
import { GameBoardHeader } from "./gameBoard/GameBoardHeader";
import { JudgingSection } from "./gameBoard/JudgingSection";
import { PlayerSubmissionSection } from "./gameBoard/PlayerSubmissionSection";
import { useGameBoard } from "./gameBoard/useGameBoard";
import { GameOverScreen } from "./GameOverScreen";
import { NpcControl } from "./NpcControl";
import { PatientCase } from "./PatientCase";
import { Scoreboard } from "./Scoreboard";
import { TreatmentCardList } from "./TreatmentCard";
import { TreatmentSubmissions } from "./TreatmentSubmissions";
import { WinnerDisplay } from "./WinnerDisplay";
import { triggerActiveSessionRefresh } from "../hooks/useActiveSession";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import { useGameStore } from "../store/gameStore";
import { GameStatusEnum, type GameState, type AnswerCard } from "../types/game";

export function GameBoard() {
  const {
    sessionId,
    roomCode,
    playerId,
    gameState,
    updateGameState,
    setSessionError,
    clearSession,
  } = useGameStore();
  const state = gameState as GameState | null;

  const {
    selectedCardIndices,
    selectedAnswerIndex,
    submitting,
    confettiFiredRef,
    npcConfig,
    showNpcControl,
    showMagicCode,
    showLeaveConfirm,
    wsStatus,
    npcActionLog,
    setNpcConfig,
    setShowMagicCode,
    setShowLeaveConfirm,
    handleSubmitCards,
    handleSelectAnswer,
    handleSelectWinner,
    toggleCardSelection,
    sendMessage,
    clearNpcLog,
  } = useGameBoard({
    sessionId: sessionId!,
    playerId: playerId!,
    gameState: state,
    updateGameState,
    setSessionError,
  });

  // Handle leaving the game - notify server then clear local state
  const handleLeaveGame = useCallback(async () => {
    try {
      await api.leaveMySession();
      triggerActiveSessionRefresh();
      logger.info("game_left", { sessionId, playerId });
    } catch (error) {
      logger.error("leave_game_failed", { error, sessionId, playerId });
      // Continue with local cleanup even if server call fails
    }
    clearSession();
    window.location.href = "/";
  }, [sessionId, playerId, clearSession]);

  const { fireworks } = useCelebration();

  // Fire confetti when game ends
  useEffect(() => {
    if (
      state?.room_status === GameStatusEnum.GAME_OVER &&
      !confettiFiredRef.current
    ) {
      confettiFiredRef.current = true;
      fireworks();

      toast.success("Game Over!", {
        description: "Check the final scores below",
        duration: 5000,
      });
    }
  }, [state?.room_status, confettiFiredRef, fireworks]);

  if (!state) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: "var(--color-background)" }}
      >
        <div
          className="rounded-neo-xs p-8 text-center"
          style={{
            backgroundColor: "var(--color-surface)",
            border: "6px solid var(--color-text-primary)",
            boxShadow: "12px 12px 0px rgba(0, 0, 0, 0.2)",
          }}
        >
          <div
            className="animate-spin h-8 w-8 border-4 rounded-full mx-auto mb-4"
            style={{
              borderColor: "var(--color-text-primary)",
              borderTopColor: "transparent",
            }}
          ></div>
          <p style={{ color: "var(--color-text-primary)", fontWeight: "bold" }}>
            Loading game...
          </p>
        </div>
      </div>
    );
  }

  const isJudge = state.is_judge;
  const currentStatus = state.room_status;
  const myActionStatus = state.player_action_statuses?.[playerId!] || "idle";

  return (
    <div className="min-h-screen pb-8" data-testid="game-board">
      <GameBoardHeader
        currentRound={state.current_round}
        currentStatus={currentStatus}
        wsStatus={wsStatus}
        isJudge={isJudge}
        roomCode={roomCode}
        sessionId={sessionId}
        showMagicCode={showMagicCode}
        onToggleMagicCode={() => setShowMagicCode((prev) => !prev)}
        onLeaveGame={() => setShowLeaveConfirm(true)}
      />

      {/* Mode Banner - Show for non-default modes */}
      {state.game_id &&
        state.game_id !== "terrible-psychiatrist" &&
        state.game_id !== "terrible-psychologist" && (
          <div
            className="py-2 px-4 text-center font-bold border-b-4"
            style={{
              backgroundColor: "var(--color-surface)",
              borderColor: "var(--color-primary)",
            }}
          >
            <div className="flex items-center justify-center gap-2">
              <span style={{ color: "var(--color-primary)" }}>
                Mode:{" "}
                {state.game_id
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
              <span
                style={{ color: "var(--color-text-primary)" }}
                className="text-sm"
              >
                (Classic gameplay active - mode-specific features coming soon)
              </span>
            </div>
          </div>
        )}

      <div className="container mx-auto max-w-7xl px-4 py-6">
        {/* Scoreboard */}
        <Scoreboard
          players={state.all_players}
          currentPlayerId={playerId!}
          playerActionStatuses={state.player_action_statuses || {}}
        />

        {/* Prompt Display */}
        <PatientCase promptElements={state.prompt_elements} />

        {/* Treatments Section - Shows submitted treatment cards as backs or revealed */}
        {currentStatus !== GameStatusEnum.JUDGE_ADDS_ANSWER_TO_PROMPT &&
          currentStatus !== GameStatusEnum.GAME_OVER &&
          Object.keys(state.player_submissions).length > 0 && (
            <div
              className="rounded-neo-xs p-6 mb-6"
              style={{
                backgroundColor: "var(--color-surface)",
                border: "6px solid var(--color-text-primary)",
                boxShadow: "12px 12px 0px rgba(0, 0, 0, 0.2)",
              }}
            >
              <h2
                className="font-display text-2xl font-bold mb-4"
                style={{ color: "var(--color-text-primary)" }}
              >
                Treatments
              </h2>
              <TreatmentSubmissions
                submissions={Object.entries(state.player_submissions)
                  .filter(([pid]) => pid !== playerId || isJudge)
                  .map(([pid, cards]) => {
                    const player = state.all_players.find(
                      (p) => p.player_id === pid,
                    );
                    return {
                      playerId: pid,
                      playerName: player?.display_name || "Unknown",
                      cards: cards,
                      isRevealed:
                        currentStatus === GameStatusEnum.JUDGE_PICKS_WINNER,
                    };
                  })}
                selectable={
                  isJudge && currentStatus === GameStatusEnum.JUDGE_PICKS_WINNER
                }
                onCardClick={isJudge ? handleSelectWinner : undefined}
              />
            </div>
          )}

        {/* Your Response - Show selected cards before submission or after submission */}
        {!isJudge &&
          selectedCardIndices.length > 0 &&
          currentStatus === GameStatusEnum.PLAYERS_SUBMIT_CARDS && (
            <div
              className="rounded-neo-xs p-6 mb-6"
              style={{
                backgroundColor: "var(--color-surface)",
                border: "6px solid var(--color-success)",
                boxShadow: "12px 12px 0px rgba(0, 0, 0, 0.2)",
              }}
            >
              <h2
                className="font-display text-2xl font-bold mb-4"
                style={{ color: "var(--color-text-primary)" }}
              >
                Your Response
              </h2>
              <p
                className="text-sm font-semibold mb-3"
                style={{ color: "var(--color-text-primary)" }}
              >
                Click a card to deselect it
              </p>
              <TreatmentCardList
                cards={selectedCardIndices.map(
                  (index) => state.your_hand[index],
                )}
                onCardClick={(cardIndex) => {
                  const originalIndex = selectedCardIndices[cardIndex];
                  toggleCardSelection(originalIndex);
                }}
              />
            </div>
          )}

        {/* Psychologist Answer Selection */}
        {isJudge &&
          currentStatus === GameStatusEnum.JUDGE_ADDS_ANSWER_TO_PROMPT && (
            <AnswerSelectionSection
              answerOptions={(state.judge_answer_options || []) as AnswerCard[]}
              selectedAnswerIndex={selectedAnswerIndex}
              onSelectAnswer={handleSelectAnswer}
            />
          )}

        {/* Player Submissions */}
        {!isJudge && currentStatus === GameStatusEnum.PLAYERS_SUBMIT_CARDS && (
          <PlayerSubmissionSection
            yourHand={state.your_hand}
            selectedCardIndices={selectedCardIndices}
            onCardClick={toggleCardSelection}
            onSubmitCards={handleSubmitCards}
            submitting={submitting}
            myActionStatus={myActionStatus}
          />
        )}

        {/* Psychologist Judging */}
        {currentStatus === GameStatusEnum.JUDGE_PICKS_WINNER && (
          <JudgingSection
            isJudge={isJudge}
            playerId={playerId!}
            playerSubmissions={state.player_submissions}
            allPlayers={state.all_players}
            onSelectWinner={handleSelectWinner}
            submitting={submitting}
          />
        )}

        {/* Winner Display */}
        {state.winning_submission_info && (
          <WinnerDisplay
            winningInfo={state.winning_submission_info}
            players={state.all_players}
          />
        )}

        {/* Your Treatments (always visible for non-psychologists) */}
        {!isJudge && currentStatus !== GameStatusEnum.PLAYERS_SUBMIT_CARDS && (
          <div
            className="rounded-neo-xs p-6"
            style={{
              backgroundColor: "var(--color-surface)",
              border: "6px solid var(--color-text-primary)",
              boxShadow: "12px 12px 0px rgba(0, 0, 0, 0.2)",
            }}
          >
            <h2
              className="font-display text-2xl font-bold mb-4"
              style={{ color: "var(--color-text-primary)" }}
            >
              Your Treatments
            </h2>
            <TreatmentCardList
              cards={state.your_hand}
              emptyMessage="No treatments in hand"
            />
          </div>
        )}

        {/* Game Over Screen */}
        {currentStatus === GameStatusEnum.GAME_OVER && (
          <GameOverScreen
            players={state.all_players}
            currentPlayerId={playerId!}
            onLeaveGame={handleLeaveGame}
          />
        )}
      </div>

      {/* NPC Control Panel */}
      {showNpcControl && (
        <NpcControl
          config={npcConfig}
          onConfigChange={setNpcConfig}
          actionLog={npcActionLog}
          onClearLog={clearNpcLog}
        />
      )}

      {/* Leave Game Confirmation Modal */}
      <ConfirmModal
        isOpen={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        onConfirm={handleLeaveGame}
        title="Leave Game"
        message="Are you sure you want to leave? This cannot be undone."
        confirmText="Leave"
        cancelText="Stay"
        variant="danger"
      />

      {/* Chat Panel */}
      <ChatPanel sendMessage={sendMessage} />
    </div>
  );
}
